'use client';

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Calendar,
  Clock,
  Repeat,
  XCircle,
  RefreshCw,
  FileText,
  AlertTriangle,
  CheckCircle,
  Info,
} from 'lucide-react';
import { useToast } from '@crm-src/hooks/use-toast';
import { scheduledInvoicesApi } from '@crm-src/lib/invoice-schedules-api';
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageLoader } from "@/components/ui/page-loader";
import { BackButton } from "@/components/ui/back-button";

// Spinner sized to match the old `animate-spin` lucide icons in inline chips.
const spin = { animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } } as const;

interface ScheduledInvoiceDetail {
  id: string;
  scheduled_date: string;
  status: string;
  generated_at?: string;
  cancelled_at?: string;
  source_invoice?: {
    id: string;
    invoice_number: string;
    total_amount: number;
    customer_name?: string;
  };
  generated_invoice?: {
    id: string;
    invoice_number: string;
    total_amount: number;
  };
  invoice_schedule?: {
    id: string;
    name: string;
    frequency: string;
    status: string;
  };
  reminder_task?: {
    id: string;
    name: string;
    status: string;
    due_date: string;
  };
}

export default function ScheduledInvoiceDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const scheduledId = params.id as string;

  const [scheduled, setScheduled] = useState<ScheduledInvoiceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  useEffect(() => {
    if (scheduledId) {
      loadScheduledInvoice();
    }
  }, [scheduledId]);

  const loadScheduledInvoice = async () => {
    try {
      setIsLoading(true);
      const response = await scheduledInvoicesApi.getById(scheduledId);
      if (response.success && response.data) {
        setScheduled(response.data as any);
      } else {
        toast({
          title: "Error",
          description: "Scheduled invoice not found",
          variant: "destructive",
        });
        navigate(-1);
      }
    } catch (error) {
      console.error('Error loading scheduled invoice:', error);
      toast({
        title: "Error",
        description: "Failed to load scheduled invoice",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setIsActionLoading(true);
      const response = await scheduledInvoicesApi.cancel(scheduledId);
      if (response.success) {
        toast({
          title: "Success",
          description: "Scheduled invoice cancelled",
        });
        loadScheduledInvoice();
      } else {
        toast({
          title: "Error",
          description: response.errors?.[0] || "Failed to cancel",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsActionLoading(false);
      setShowCancelDialog(false);
    }
  };

  const spinningRefresh = <Box component="span" sx={{ display: "flex", ...spin }}><RefreshCw size={12} /></Box>;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip size="small" variant="outlined" color="primary" icon={<Clock size={12} />} label="Scheduled" />;
      case 'generating':
        return <Chip size="small" variant="outlined" color="warning" icon={spinningRefresh} label="Generating" />;
      case 'generated':
        return <Chip size="small" variant="outlined" color="success" icon={<CheckCircle size={12} />} label="Generated" />;
      case 'failed':
        return <Chip size="small" color="error" icon={<AlertTriangle size={12} />} label="Failed" />;
      case 'cancelled':
        return <Chip size="small" color="default" icon={<XCircle size={12} />} label="Cancelled" />;
      default:
        return <Chip size="small" variant="outlined" label={status} />;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (!scheduled) {
    return (
      <Box sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center", color: "text.secondary" }}>
              <Typography>Scheduled invoice not found</Typography>
              <BackButton sx={{ mt: 2 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // A scheduled invoice can be cancelled while it is still upcoming / freshly
  // generated (mirrors ScheduledInvoice#can_cancel? in crm-api).
  const canCancel = ['pending', 'generating', 'generated'].includes(scheduled.status);

  return (
    <Box sx={{ py: 4, maxWidth: 896, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <BackButton />
          <Box>
            <Typography component="h1" sx={{ fontSize: "1.5rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
              <Calendar size={24} />
              Scheduled Invoice
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              {scheduled.source_invoice?.invoice_number || 'Invoice pending'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {getStatusBadge(scheduled.status)}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gap: 3 }}>
        {/* How it's handled */}
        <Box sx={{ display: "flex", gap: 1.5, p: 2, borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "surfaceSoft" }}>
          <Box component="span" sx={{ display: "flex", color: "primary.light", mt: "2px" }}><Info size={18} /></Box>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            This invoice is generated automatically on its scheduled date and a review copy is emailed to
            finance. Nothing is sent to the customer automatically — review the generated invoice and send it
            manually if appropriate.
          </Typography>
        </Box>

        {/* Main Info Card */}
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Clock size={20} />
                <Typography component="span" sx={{ fontWeight: 600 }}>Schedule Details</Typography>
              </Box>
            }
          />
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Scheduled Date */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, p: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.08), borderRadius: 1 }}>
              <Box sx={{ color: "primary.main", mt: "2px", display: "flex" }}><Calendar size={20} /></Box>
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Scheduled For</Typography>
                <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "primary.dark" }}>
                  {formatDate(scheduled.scheduled_date)}
                </Typography>
              </Box>
            </Box>

            {/* Source Invoice */}
            {scheduled.source_invoice && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                <Box sx={{ color: "text.disabled", mt: "2px", display: "flex" }}><FileText size={20} /></Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Template Invoice</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={{ fontWeight: 500 }}>{scheduled.source_invoice.invoice_number}</Typography>
                    <Typography sx={{ fontWeight: 600, color: "success.main" }}>
                      ${Number(scheduled.source_invoice.total_amount || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  {scheduled.source_invoice.customer_name && (
                    <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{scheduled.source_invoice.customer_name}</Typography>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => navigate(`/crm/invoices/${scheduled.source_invoice?.id}`)}
                >
                  View Invoice
                </Button>
              </Box>
            )}

            {/* Generated Invoice */}
            {scheduled.generated_invoice && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 1.5, bgcolor: (t) => alpha(t.palette.success.main, 0.08), borderRadius: 1 }}>
                <Box sx={{ color: "success.main", mt: "2px", display: "flex" }}><CheckCircle size={20} /></Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Generated Invoice</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={{ fontWeight: 500, color: "success.dark" }}>{scheduled.generated_invoice.invoice_number}</Typography>
                    <Typography sx={{ fontWeight: 600, color: "success.main" }}>
                      ${Number(scheduled.generated_invoice.total_amount || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  {scheduled.generated_at && (
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Generated {formatDate(scheduled.generated_at)}</Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  onClick={() => navigate(`/crm/invoices/${scheduled.generated_invoice?.id}`)}
                >
                  Review & Send
                </Button>
              </Box>
            )}

            {/* Recurring Schedule Info */}
            {scheduled.invoice_schedule && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 1.5, bgcolor: (t) => alpha(t.palette.secondary.main, 0.08), borderRadius: 1 }}>
                <Box sx={{ color: "secondary.main", mt: "2px", display: "flex" }}><Repeat size={20} /></Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Part of Recurring Schedule</Typography>
                  <Typography sx={{ fontWeight: 500, color: "secondary.dark" }}>{scheduled.invoice_schedule.name}</Typography>
                  <Typography sx={{ fontSize: 14, color: "secondary.main", textTransform: "capitalize" }}>{scheduled.invoice_schedule.frequency}</Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => navigate(`/crm/invoices/schedules/${scheduled.invoice_schedule?.id}`)}
                >
                  View Schedule
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Card */}
        {canCancel && (
          <Card>
            <CardHeader
              title={<Typography component="span" sx={{ fontWeight: 600 }}>Actions</Typography>}
              subheader="Cancel this scheduled invoice if it should not be generated."
            />
            <CardContent sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setShowCancelDialog(true)}
                disabled={isActionLoading}
                startIcon={isActionLoading ? <CircularProgress size={16} color="inherit" /> : <XCircle size={16} />}
              >
                Cancel Scheduled Invoice
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        title="Cancel Scheduled Invoice?"
        description="This stops the invoice from being generated on its scheduled date. You can set up a new schedule later."
        confirmLabel="Yes, Cancel"
        cancelLabel="Keep Scheduled"
        onConfirm={handleCancel}
      />
    </Box>
  );
}
